import { Link } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import * as React from "react";

import { useNavigation } from "@/providers";
import { basename } from "@/shared/domain/files/filesystem";
import { useFile } from "@/shared/domain/files/hooks/useFile.ts";
import { LoggerService } from "@/shared/services";

interface FileVizLinkProps {
  fileId: string;

  /**
   * If provided, the file will be opened at the given time.
   * Otherwise, the file will be opened at the start of the file.
   *
   * `startTime` is in nanoseconds since the UNIX epoch.
   **/
  startTime?: bigint;

  /**
   * If provided, the topics will be filtered by this term.
   **/
  filterTerm?: string;

  /**
   * Optional text content to display below the link
   **/
  subtitle?: React.ReactNode;
}

export const FileVizLink: React.FC<FileVizLinkProps> = ({
  fileId,
  startTime,
  filterTerm,
  subtitle,
}) => {
  const fileQuery = useFile(fileId);
  const { goto } = useNavigation();

  if (fileQuery.isLoading) {
    return (
      <Typography variant="body2">
        <Skeleton variant="text" width="200px" />
      </Typography>
    );
  }

  if (fileQuery.isError || fileQuery.data === undefined) {
    LoggerService.error(fileQuery.error?.message, fileQuery.error);
    return (
      <Typography variant="body2" color="error">
        Failed to load event scope
      </Typography>
    );
  }

  const handleClick = () => {
    goto.visualize({
      dataSource: {
        files: [fileId],
      },
      params: {
        filterTerm,
        t: startTime,
      },
    });
  };

  return (
    <>
      <Link
        onClick={handleClick}
        component="button"
        underline="hover"
        variant="body2"
        title={fileQuery.data.relative_path}
        sx={{ userSelect: "text" }}
      >
        {basename(fileQuery.data.relative_path)}
      </Link>
      {subtitle}
    </>
  );
};
